/**
 * CustomerDetailsPage
 *
 * Usage Notes:
 * - Expects a `:userFanId` route param (number) to fetch customer context.
 * - Relies on audience feature hooks for data loading.
 * - Wrapped by `MainLayout` from router; no outlet context needed.
 */
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useGlobalStore } from '@/stores/globalStore';
import { CustomerOverviewCard, CustomerSubscriptionsSection } from '@/features/audience/components';
import { IncomeOrders, type IncomeOrdersRef } from '@/features/income/components/IncomeOrders';
import { useCustomerSubscriptions } from '@/features/audience/hooks/useCustomerSubscriptions';
import { useCustomerDetails } from '@/features/audience/queries';
import { useStanRouter } from '@/hooks/useStanRouter';

export default function CustomerDetailsPage() {
    const { userFanId } = useParams();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const userFanIdParsed = Number(userFanId);
    const { push } = useStanRouter();
    const { customer, isLoading, refetch: refreshCustomer } = useCustomerDetails(userFanIdParsed);
    const incomeOrdersRef = useRef<IncomeOrdersRef>(null);

    const { subscriptions, hasActiveSubscription } = useCustomerSubscriptions(customer);

    // Update breadcrumbs when customer data is loaded
    useEffect(() => {
        if (customer) {
            setBreadcrumbs([
                { title: 'My Customers', to: '/audience', id: 'customers' },
                {
                    title: customer.fullName || 'Overview',
                    to: `/audience/details/${userFanIdParsed}`,
                    active: true,
                    id: 'customer-details',
                },
            ]);
        }
    }, [customer, userFanIdParsed, setBreadcrumbs]);

    if (Number.isNaN(userFanIdParsed) || userFanIdParsed <= 0 || (!isLoading && !customer)) {
        push('/audience');
        return null;
    }

    // Show loading state while customer data is being fetched
    if (isLoading || !customer) {
        return (
            <div className='flex flex-col gap-8'>
                <div className='shadow-nucleus-elevation-medium mt-4 rounded-lg bg-white p-8'>
                    <div className='text-center'>Loading customer details...</div>
                </div>
            </div>
        );
    }

    const handleInvoiceSent = () => {
        // Refresh income orders when invoice is sent successfully
        incomeOrdersRef.current?.refetch();
    };

    return (
        <div className='flex flex-col gap-8'>
            <CustomerOverviewCard
                customer={customer}
                onCustomerUpdated={refreshCustomer}
                hasActiveSubscription={hasActiveSubscription}
                onInvoiceSent={handleInvoiceSent}
            />

            <CustomerSubscriptionsSection
                subscriptions={subscriptions}
                customer={customer}
                onCustomerUpdated={refreshCustomer}
            />

            <IncomeOrders ref={incomeOrdersRef} userFanId={userFanIdParsed} customerName={customer.fullName} />
        </div>
    );
}
